import { z } from 'zod';
import {
  identityProofSchema,
  imageWithUploadSchema,
  partySchema,
  vehicleCountrySchema,
  vehicleGroupSchema,
} from '../../common';
import { idSchema, nameSchema, dateSchema, objectIdSchema } from '../../validation';
import { getVehiclePlateNumber } from '@hero/core';

/**
 * Saudi plate category (نوع اللوحة). Keys mirror the categories the traffic
 * authority issues; see {@link vehicleRegistrationSchema.plateType} for the
 * mapping onto Bayan's numeric `plateTypeId`.
 */
export const vehiclePlateTypeSchema = z.enum([
  'public_transport',
  'private_transport',
  'heavy_equipment',
  'export',
  'temporary',
]);
export type VehiclePlateType = z.infer<typeof vehiclePlateTypeSchema>;

export const vehicleOwnerTypeSchema = z.enum([
  'carrier_owns_and_operates_vehicle',
  'not_sure_yet',
  'owner_is_different_from_operator',
]);
export type VehicleOwnerType = z.infer<typeof vehicleOwnerTypeSchema>;

/**
 * What KIND of party هوية المالك names on the استمارة.
 *
 * - `company` — an establishment (70…) that owns the truck; usually the
 *   carrier itself, so its 70… is a strong `carrier.moi` candidate.
 * - `individual` — a person (1…/2…). Never a `carrier.moi`.
 * - `financier` — a bank or financing house (Al Rajhi, ALJ, …) holding
 *   the title while the truck is paid off. Its 70… is almost never the
 *   carrier, so issuance tries it LAST — but never drops it.
 */
export const vehicleOwnerKindSchema = z.enum(['company', 'individual', 'financier']);
export type VehicleOwnerKind = z.infer<typeof vehicleOwnerKindSchema>;

/**
 * @deprecated The `registration.owner` Party copy is redundant with
 * `ownerLegalName` / `ownerIdentity` / `ownerType`. Still read as a
 * fallback until the contract migration; new writes go to the flat fields.
 *
 * Re-implemented in terms of {@link partySchema} (was previously
 * `relationSchema.extend`). The Party snapshot carries the owner's
 * `roles[]` and `ids[]` for downstream consumers (compliance reads,
 * registration verification, etc.). See
 * `docs/relations-v2/02-data-model.md` section 12.
 */
export const vehicleOwnerSchema = partySchema.extend({
  /**
   * - if the owner is company, then this will be its Commercial Registration (هوية المالك)
   * - if the owner is individual, then it will be his personal id
   * - if empty ... then we still don't have it
   */
  id: idSchema.optional(),

  /**
   * sometimes a carrier is registered with us in the platform, but has a truck that he will operate but does not own.
   *
   * that's why we need to distinguish. If you're not sure ... add not_sure_yet.
   */
  ownerType: vehicleOwnerTypeSchema,
});
export type VehicleOwner = z.infer<typeof vehicleOwnerSchema>;

export const saudiVehiclePlateSchema = z.custom<string>().transform((plate, ctx) => {
  try {
    const plateNumber = nameSchema.parse(plate);
    const { fullPlate, letters, numbers } = getVehiclePlateNumber(plateNumber);
    // @TBC -- this plate validation might cause an issue when we do cross border
    // because foreign vehicles have diff plate structure such as all numbers or diff letters/numbers length

    // plate must include 4 numbers
    nameSchema.min(4).max(4).parse(numbers.ar);
    nameSchema.min(4).max(4).parse(numbers.en);

    // plate must include 3 numbers --- 5 letters in arabic because of the spaces
    nameSchema.min(5).max(5).parse(letters.ar);
    nameSchema.min(3).max(3).parse(letters.en);

    plate = fullPlate;
  } catch (error) {
    ctx.addIssue({
      code: 'custom',
      message: 'vehicle_plate_is_not_valid',
    });
  }
  return plate;
});

/**
 * RAW reads of the الاستمارة (رخصة السير) — what the document reader saw,
 * kept verbatim. Editable, never a hard requirement, never proof.
 *
 * The three layers of a truck's identity:
 *
 * 1. `registration.extraction.*` — raw reads (this object).
 * 2. `registration.plate` / `plateType` / `ownerIdentity` /
 *    `operatorIdentity` / `ownerKind` / `ownerLegalName` — the CURRENT
 *    values, promoted from a read or typed / edited by a person.
 * 3. `registration.carrierIdentity` + `registration.identityProof` — what
 *    was proven. See `docs/adr/011-identity-fields.md`.
 */
export const vehicleRegistrationExtractionSchema = z.object({
  source: z.enum(['agent_vision', 'user_typed']).optional(),
  extractedAt: dateSchema.optional(),
  /**
   * @deprecated Same printed field as `operatorIdentity` (هوية المستخدم
   * when it is a 70…). Read as a fallback for `operatorIdentity` until the
   * contract migration; never written.
   */
  vehicleUserEstablishmentId: idSchema.optional(),
  /**
   * هوية المالك AS READ — the owner's number (70… company / bank, or a
   * person's 1…/2…). Read by `rankCarrierMoiCandidates` (raw-read tier,
   * or last when `ownerKind` is `financier`).
   */
  ownerIdentity: idSchema.optional(),
  /**
   * هوية المستخدم AS READ — who operates the truck: a person (1…/2…,
   * usually the driver) OR an establishment (70…). A 70… here is a strong
   * `carrier.moi` candidate (raw-read tier).
   */
  operatorIdentity: idSchema.optional(),
  /** The owner's kind as read — see {@link vehicleOwnerKindSchema}. */
  ownerKind: vehicleOwnerKindSchema.optional(),
  /** Owner name exactly as printed. */
  ownerName: nameSchema.optional(),
  /** @deprecated Renamed to `ownerName`. Read as a fallback only. */
  ownerNameRaw: nameSchema.optional(),
  /** Body-type / color words the reader could not map onto Hero enums. */
  vehicleTypeRaw: z.string().trim().min(1).optional(),
  colorRaw: z.string().trim().min(1).optional(),
  /** المستخدم name printed on رخصة سير, when the card shows one. */
  operatorName: nameSchema.optional(),
  /** رقم الهيكل (VIN / chassis) as printed. */
  chassisNumber: z.string().trim().min(1).optional(),
  /** الرقم التسلسلي as printed. */
  serialNumber: z.string().trim().min(1).optional(),
  /** ماركة المركبة as printed (mapped onto `classification.brand` only when obvious). */
  brandRaw: z.string().trim().min(1).optional(),
  /** سنة الصنع as printed. */
  modelYear: z.number().int().min(1980).max(2100).optional(),
  /** نوع التسجيل / نوع اللوحة as printed (نقل عام / خصوصي …) — `plateType` holds the mapping. */
  plateTypeRaw: z.string().trim().min(1).optional(),
  /**
   * الحمولة / الوزن as printed (kg or free text). Older reads put either
   * the payload («حمولة المركبة: 3») or the truck's own weight («2690
   * كجم») here — `payloadRaw` / `vehicleWeightRaw` split them.
   */
  loadCapacityRaw: z.string().trim().min(1).optional(),
  /** حمولة المركبة — what the truck may carry, as printed (often tons, no unit). */
  payloadRaw: z.string().trim().min(1).optional(),
  /** الوزن / وزن المركبة / الوزن الإجمالي — the truck's own weight, as printed. Never cargo. */
  vehicleWeightRaw: z.string().trim().min(1).optional(),
  /**
   * Truck group the printed body word implies (راس / تريلا → truck,
   * لوري → lorry, دينا / ثلاجة / ريوارد → dyna, بكب → pickup).
   */
  vehicleGroup: vehicleGroupSchema.optional(),
  /** تاريخ انتهاء رخصة السير as printed (`expiryDate` holds the parsed date). */
  registrationExpiryRaw: z.string().trim().min(1).optional(),
  /** Full raw reader output (audit / training). */
  raw: z.record(z.unknown()).optional(),
  /** @deprecated Use `registration.identityProof` with `by: 'staff'`. Read as a fallback only. */
  verifiedBy: z
    .object({
      staffUserId: objectIdSchema,
      displayName: nameSchema.optional(),
      at: dateSchema,
    })
    .optional(),
});
export type VehicleRegistrationExtraction = z.infer<typeof vehicleRegistrationExtractionSchema>;

export const vehicleRegistrationSchema = z.object({
  /** the image of the registration. Optional — recommended for Logisti integration. */
  image: imageWithUploadSchema.optional(),

  /** the registration/origin country of the vehicle. Optional — recommended for Logisti. */
  country: vehicleCountrySchema.optional(),

  /** the plate number of the vehicle such as ABC123. This will always be in `English`.
   *
   * Hard requirement on vehicle creation (alongside `classification.type`).
   */
  plate: saudiVehiclePlateSchema,

  /** استمارة expiry date. Optional — captured from photos when visible. */
  expiryDate: dateSchema.optional(),

  /** اسم المالك — the CURRENT owner name (company, person, or financier). Display / refused-numbers label. */
  ownerLegalName: nameSchema.optional(),
  /**
   * هوية المالك — the CURRENT owner number: a 70… company / bank, or a
   * person's 1…/2….
   *
   * Written by: promotion from `extraction.ownerIdentity`, the vehicle
   * form, or staff. Read by: `rankCarrierMoiCandidates` — vehicle tier
   * when it is a 70… and `ownerKind !== 'financier'`, LAST when it is a
   * financier (never dropped).
   * Not for: "the number Bayan accepted" — that is `carrierIdentity`.
   */
  ownerIdentity: idSchema.optional(),
  /**
   * What kind of party the owner is — see {@link vehicleOwnerKindSchema}.
   * Decides where `ownerIdentity` ranks. Unset = treated as `company` when
   * the number is a 70….
   */
  ownerKind: vehicleOwnerKindSchema.optional(),
  /**
   * Does the account operate a truck it owns, or one owned by someone
   * else? Moved up from the deprecated `owner.ownerType`. Drives the
   * dashboard's "owned by another company" UI and `isCarrierSameAsOwner`.
   */
  ownerType: vehicleOwnerTypeSchema.optional(),
  /**
   * هوية المستخدم — the CURRENT operator: a person (1…/2…, usually the
   * driver) OR an establishment (70…).
   *
   * Written by: promotion from `extraction.operatorIdentity`, the vehicle
   * form, or staff (a 70… staff type on a truck lands here). Read by:
   * `rankCarrierMoiCandidates` — the FIRST non-proven slot when it is a
   * 70…; a person's id is skipped.
   * Not for: the accepted number — that is `carrierIdentity`.
   */
  operatorIdentity: idSchema.optional(),
  /**
   * الرقم الموحد Bayan ACCEPTED as `carrier.moi` for this truck — the
   * same value as `Driver.legal.carrierIdentity` on the accepted filing.
   * Not printed on the استمارة.
   *
   * Written by: issuance only, on a successful `createTrip`
   * (`recordIdentityProof`). Read by: `rankCarrierMoiCandidates` (proven
   * tier, first) and the "Accepted by Bayan" badge.
   * Not for: anything typed. Staff never write this field — a typed 70…
   * goes to `operatorIdentity`.
   */
  carrierIdentity: idSchema.optional(),
  /**
   * The last proven filing for this truck — the SAME snapshot the driver
   * carries (`Driver.legal.identityProof`), including which driver it was
   * proven with. Compare with `isVehicleIdentityProven` / `isPairProven`
   * from `@hero/core`. See {@link identityProofSchema}.
   */
  identityProof: identityProofSchema.optional(),
  /**
   * @deprecated Redundant Party copy — use `ownerLegalName` /
   * `ownerIdentity` / `ownerType`. Read as a fallback until the contract
   * migration.
   */
  owner: vehicleOwnerSchema.optional(),

  /**
   * this could be the serial number for Saudi vehicle registration.
   *
   * It's optional because we can find the car by its plate, but we might use it in the future.
   * */
  serialNum: idSchema.optional(),

  /** رقم الهيكل */
  structureNumber: idSchema.optional(),

  /**
   * Plate category as classified by the Saudi traffic authority
   * (نوع اللوحة). Maps 1:1 onto Bayan's `vehicle.plateTypeId` (appendix 6.1):
   *
   * | value              | Bayan id | Arabic       |
   * | ------------------ | -------- | ------------ |
   * | `public_transport` | 2        | نقل عام      |
   * | `private_transport`| 3        | نقل خاص      |
   * | `heavy_equipment`  | 7        | معدات ثقيلة  |
   * | `export`           | 8        | تصدير        |
   * | `temporary`        | 11       | مؤقت         |
   *
   * Stored as the readable key rather than the numeric id so the value stays
   * meaningful outside the Bayan context; `BAYAN_PLATE_TYPES` in `@hero/core`
   * does the translation at the wire boundary.
   *
   * Optional — existing vehicles pre-date the field. When unset, issuance
   * falls back to `BAYAN_CONFIG.defaults.plateType` (`public_transport`),
   * which is the category a truck moving third-party freight carries; an
   * explicit value here always wins. Issuance only blocks with
   * `missing_vehicle_plate_type` if that default is cleared too.
   */
  plateType: vehiclePlateTypeSchema.optional(),

  /**
   * Audit snapshot of what the document reader extracted from the
   * الاستمارة. Additive and always optional — see
   * {@link vehicleRegistrationExtractionSchema}.
   */
  extraction: vehicleRegistrationExtractionSchema.optional(),
});
